import { GridColDef } from "@mui/x-data-grid";
import DataTableDefault from "../../../../../components/DataTable";
import DatatableModal, { ModalDataProps } from "./DatatableModal";
import { useState, useEffect } from "react";
import { Button } from "@mui/material";
import { translate, getTableName, translateRelation, translateByField } from "../../../../utils/translation";
import { useTranslation } from "../../hooks/translation.hook";
import { formatDate, isValidDate } from "../../../../utils/date";
import { isConvertibleToNumber } from "../../../../utils/strings";
import { useCustomReport } from "../../hooks/customReportData.hook";
import { findFieldsRelations } from "../../../../../services/translation";

interface DataTableProps {
  data: Array<any>;
  ID_RELATORIO: number;
  reportStructure: Array<any>;
  [x: string]: any;
}

export default function DataTable({
  data,
  dataRaw,
  reportStructure,
  ID_RELATORIO,
  relations: relationsProp,
  fieldRelations: fieldRelationsProp,
  preferOriginAlias,
  ...props
}: DataTableProps) {
  const { translations, translationsFields } = useTranslation();
  const { relations: relationsContext } = useCustomReport();
  const [fieldRelationsState, setFieldRelationsState] = useState<Array<any>>([]);

  const relations = relationsProp ?? relationsContext ?? [];
  const fieldRelations = fieldRelationsProp?.length ? fieldRelationsProp : fieldRelationsState;

  useEffect(() => {
    if (fieldRelationsProp?.length) return;
    const fetchFieldRelations = async () => {
      try {
        const result = await findFieldsRelations();
        setFieldRelationsState(result || []);
      } catch (error) {
        console.error("Error fetching field relations:", error);
      }
    };
    fetchFieldRelations();
  }, [fieldRelationsProp?.length]);

  let columns: GridColDef[] = [];
  const [open, setOpen] = useState(false);
  const [modalData, setModalData] = useState<ModalDataProps>({
    title: '',
    data: [{}],
  });

  const openMoreInformation = (key: string, value: [{}]) => {
    setOpen(true);
    let title: string = key;
    if (key === "invoiceItemsPo" && Array.isArray(value) && value.length > 0) {
      const first = value[0] as Record<string, unknown>;
      const country = first?.ID_PAIS_ORIGEM ?? first?.SIGLA_PAIS;
      const label =
        translations && translate("GTS_INVOICE_ITEM_PO", "ID_PAIS_ORIGEM", translations) !== "ID_PAIS_ORIGEM"
          ? translate("GTS_INVOICE_ITEM_PO", "ID_PAIS_ORIGEM", translations)
          : "PN Country of Origin";
      title = country != null && String(country).trim() !== "" ? `${label}: ${country}` : label;
    }
    setModalData({
      title,
      data: value,
      fieldKey: key,
    });
  }

  const processData = (param: any) => {
    if(isValidDate(param)) {
      return formatDate(param);
    }
    if(isConvertibleToNumber(param)) {
      if(typeof param === 'number') {
        return param.toString().replace('.', ',');
      }
      return param.replace('.', ',');
    }
    return param;
  }

  // Para chaves sem relação (ex: ID_PAIS_ORIGEM), preferir o campo do contexto: invoice (tabela principal) ou invoiceItemsPo (modal de itens).
  const preferRootField = (m: { NOME_TABELA_ORIGEM_ALIAS?: string; NOME_TABELA_ORIGEM?: string }) => {
    const alias = m.NOME_TABELA_ORIGEM_ALIAS ?? "";
    if (preferOriginAlias) {
      return alias === preferOriginAlias || m.NOME_TABELA_ORIGEM === (preferOriginAlias === "invoiceItemsPo" ? "GTS_INVOICE_ITEM_PO" : preferOriginAlias === "invoice" ? "GTS_INVOICE" : undefined);
    }
    if (alias === "" || alias === "invoice") return true;
    return m.NOME_TABELA_ORIGEM === "GTS_INVOICE";
  };

  const getCustomReportFields = (reportStructure: Array<any>) => {
    if (!reportStructure) return [];
    const rs = reportStructure as any;
    const first = Array.isArray(reportStructure) ? reportStructure[0] : rs;
    return (first?.customReportFields ?? rs?.customReportFields) ?? [];
  };

  const findOrder = (key: string, reportStructure: Array<any>) => {
    const keySplited = key.split('.');
    const fieldName = keySplited[keySplited.length - 1];
    const relationSegments = keySplited.length > 1 ? keySplited.slice(0, -1) : [];
    const customReportFields = getCustomReportFields(reportStructure);
    const isRootKey = relationSegments.length === 0;

    let matches = customReportFields.filter((structureItem: any) => structureItem.NOME_ATRIBUTO === fieldName);
    let order = matches.length === 1 ? matches[0]?.ORDER : undefined;
    if (matches.length > 1 && isRootKey) {
      const rootMatch = matches.find((m: any) => preferRootField(m));
      order = rootMatch?.ORDER ?? matches[0]?.ORDER;
    } else if (order == null && matches.length > 0) {
      order = matches[0]?.ORDER;
    }
    if (order == null) {
      matches = customReportFields.filter((structureItem: any) => structureItem.NOME_TABELA_ALIAS === fieldName);
      order = matches.length === 1 ? matches[0]?.ORDER : undefined;
      if (matches.length > 1 && isRootKey) {
        const rootMatch = matches.find((m: any) => preferRootField(m));
        order = rootMatch?.ORDER ?? matches[0]?.ORDER;
      } else if (order == null && matches.length > 0) {
        order = matches[0]?.ORDER;
      }
    }
    if (order == null && relationSegments.length > 0) {
      const segmentsFromClosest = [...relationSegments].reverse();
      order = segmentsFromClosest.reduce(
        (acc: number | undefined, alias) =>
          acc ?? customReportFields.find((structureItem: any) => structureItem.NOME_TABELA_ALIAS === alias)?.ORDER,
        undefined
      );
    }
    return order;
  }

  // Função para encontrar o campo original no reportStructure baseado na chave
  const findOriginalField = (key: string, reportStructure: Array<any>) => {
    const keySplited = key.split('.');
    const fieldName = keySplited[keySplited.length - 1];
    const relationSegments = keySplited.length > 1 ? keySplited.slice(0, -1) : [];
    const customReportFields = getCustomReportFields(reportStructure);
    const isRootKey = relationSegments.length === 0;
  
    // Tenta encontrar pelo nome do atributo
    let matches = customReportFields.filter((structureItem: any) => structureItem.NOME_ATRIBUTO === fieldName);
    
    // Se tem múltiplos matches e é campo root, prefira pelo contexto (preferOriginAlias)
    if (matches.length > 1 && isRootKey && preferOriginAlias) {
      const contextMatch = matches.find((m: any) =>
        m.NOME_TABELA_ORIGEM_ALIAS === preferOriginAlias ||
        m.NOME_TABELA_ALIAS === preferOriginAlias
      );
      if (contextMatch) {
        return contextMatch;
      }
    }
    
    let field = matches.length === 1 ? matches[0] : null;
    if (matches.length > 1 && isRootKey) {
      field = matches.find((m: any) => preferRootField(m)) ?? matches[0];
    } else if (!field && matches.length > 0) {
      field = matches[0];
    }
  
    if (field) {
      return field;
    }
  
    // Tenta encontrar pelo alias da tabela (último segmento)
    matches = customReportFields.filter((structureItem: any) => structureItem.NOME_TABELA_ALIAS === fieldName);
    field = matches.length === 1 ? matches[0] : null;
  
    if (matches.length > 1 && isRootKey) {
      field = matches.find((m: any) => preferRootField(m)) ?? matches[0];
    } else if (!field && matches.length > 0) {
      field = matches[0];
    }
  
    if (field) {
      return field;
    }
  
    // Chave com relação: busca pelo alias mais específico primeiro
    if (relationSegments.length > 0) {
      const segmentsFromClosest = [...relationSegments].reverse();
      field = segmentsFromClosest.reduce(
        (acc: any, alias) => acc ?? customReportFields.find((structureItem: any) => structureItem.NOME_TABELA_ALIAS === alias),
        null
      );
    }
  
    return field;
  };

  if(dataRaw?.length > 0) {
    const columnsSetted: Array<string> = [];

    // Coleta todas as chaves únicas das linhas, priorizando chaves com '.' (relações)
    const allKeys = new Set<string>();
    for (const dataRow of dataRaw) {
      for (const key of Object.keys(dataRow)) {
        allKeys.add(key);
      }
    }
    const sortedKeys = Array.from(allKeys).sort((a, b) => {
      const aHasRelation = a.includes('.') ? 1 : 0;
      const bHasRelation = b.includes('.') ? 1 : 0;
      if (bHasRelation !== aHasRelation) return bHasRelation - aHasRelation;
      return a.localeCompare(b);
    });

    type ColumnWithTable = GridColDef & { _tableName?: string; _relationKey?: string; _fromListOrCustom?: boolean; order?: number; minHeight?: number };
    for (const key of sortedKeys) {
      if (columnsSetted.includes(key)) continue;

      const originalField = findOriginalField(key, reportStructure);
      const result = getTableName(key, relations, ID_RELATORIO, fieldRelations);

      const tableName = result.tableName;
      
      const keySplited = key.split('.');
      const keyToTranslate = keySplited[keySplited.length - 1];
      const customLabel = originalField?.DESCRICAO ?? originalField?.NOME_CAMPO ?? originalField?.LABEL;
      const tf = (translationsFields as unknown) as Record<string, string> | undefined;
      const fromFieldsList = tf?.[key] && typeof tf[key] === 'string' && tf[key] !== key
        ? tf[key]
        : translateByField(key, translationsFields) !== key
          ? translateByField(key, translationsFields)
          : null;
      const fromReportField =
        originalField?.NOME_TABELA && originalField?.NOME_ATRIBUTO && translations
          ? translate(originalField.NOME_TABELA, originalField.NOME_ATRIBUTO, translations)
          : null;
      const isReportFieldTranslated =
        fromReportField &&
        fromReportField !== keyToTranslate &&
        fromReportField !== originalField?.NOME_ATRIBUTO &&
        fromReportField !== key;
      const defaultTranslated = translate(tableName, keyToTranslate, translations);
      const fromOriginTable =
        !isReportFieldTranslated &&
        originalField?.NOME_TABELA_ORIGEM &&
        originalField?.NOME_ATRIBUTO &&
        translations
          ? translate(originalField.NOME_TABELA_ORIGEM, originalField.NOME_ATRIBUTO, translations)
          : null;
      const originTranslated =
        fromOriginTable && fromOriginTable !== originalField?.NOME_ATRIBUTO ? fromOriginTable : null;

      // Detectar se é relacionamento (array)
      const firstRowWithKey = dataRaw.find((row: any) => row[key] !== undefined);
      const isRelationArray = Array.isArray(firstRowWithKey?.[key]);
      
      let headerText;
      if (isRelationArray) {
        // Para arrays: prioriza nome da entidade (fromFieldsList)
        headerText =
          fromFieldsList ??   
          defaultTranslated ??                                  
          (isReportFieldTranslated ? fromReportField : null) ?? 
          customLabel ??
          originTranslated;
      } else {
        // Para campos simples: prioriza tradução do reportStructure (fromReportField)
        headerText =
          (isReportFieldTranslated ? fromReportField : null) ??
          originTranslated ??
          fromFieldsList ??                                     
          customLabel ??
          defaultTranslated;
      }

      // Coluna ID_PAIS_ORIGEM no root = Invoice Country (BR). A API pode vir com NOME_TABELA_ORIGEM vazio;
      // forçar rótulo da invoice quando o campo escolhido é o da relation "invoice".
      if (
        key === "ID_PAIS_ORIGEM" &&
        originalField?.NOME_ATRIBUTO === "ID_PAIS_ORIGEM" &&
        originalField?.NOME_TABELA_ORIGEM_ALIAS === "invoice"
      ) {
        const invoiceCountryLabel =
          translations && translate("GTS_INVOICE", "ID_PAIS_ORIGEM", translations) !== "ID_PAIS_ORIGEM"
            ? translate("GTS_INVOICE", "ID_PAIS_ORIGEM", translations)
            : "Invoice Country of Origin";
        headerText = invoiceCountryLabel === "Invoice Country of Source" ? "Invoice Country of Origin" : invoiceCountryLabel;
      } else if (
        headerText === "Invoice Country of Source" &&
        originalField?.NOME_ATRIBUTO === "ID_PAIS_ORIGEM" &&
        (originalField?.NOME_TABELA === "GTS_INVOICE" || originalField?.NOME_TABELA_ORIGEM === "GTS_INVOICE")
      ) {
        headerText = "Invoice Country of Origin";
      }
      if (
        preferOriginAlias === "invoiceItemsPo" &&
        key === "ID_PAIS_ORIGEM" &&
        originalField?.NOME_ATRIBUTO === "ID_PAIS_ORIGEM" &&
        originalField?.NOME_TABELA_ORIGEM_ALIAS === "invoiceItemsPo"
      ) {
        const pnLabel =
          translations && translate("GTS_INVOICE_ITEM_PO", "ID_PAIS_ORIGEM", translations) !== "ID_PAIS_ORIGEM"
            ? translate("GTS_INVOICE_ITEM_PO", "ID_PAIS_ORIGEM", translations)
            : "PN Country of Origin";
        headerText = pnLabel;
      }

      const fromListOrCustom = isRelationArray
      ? !!(fromFieldsList ?? (isReportFieldTranslated ? fromReportField : null) ?? customLabel ?? originTranslated)
      : !!((isReportFieldTranslated ? fromReportField : null) ?? fromFieldsList ?? customLabel ?? originTranslated);
      const relationKey = key.includes('.') ? key.split('.').slice(0, -1).join('.') : undefined;

      const column: ColumnWithTable = {
        field: key,
        headerName: headerText,
        _tableName: tableName,
        _relationKey: relationKey,
        _fromListOrCustom: fromListOrCustom,
        headerClassName: "super-app-theme--header",
        flex: 1,
        minWidth: headerText.length * 10 + 15,
        minHeight: 60,
        order: findOrder(key, reportStructure),
        renderCell: (params) => {
          if(typeof params.value === 'string' || typeof params.value === 'number') {
            return processData(params.value);
          } else if((Array.isArray(params.value) && params.value.length === 0) || params.value === null || params.value === undefined) {
            return '-';
          } else if(typeof params.value === 'boolean') {
            return params.value ? 'yes' : 'no'
          } else {
            return <Button onClick={() => openMoreInformation(params.field, params.value)}>More Details</Button>;
          }
        },
      };
      columnsSetted.push(key);
      columns.push(column);
    }
  }

  // Desduplica cabeçalhos iguais só quando o rótulo não veio da lista de traduções/custom (evita prefixos em "Invoice Country of Origin", etc.)
  type ColMeta = GridColDef & { _tableName?: string; _relationKey?: string; _fromListOrCustom?: boolean };
  const headerCount = new Map<string, number>();
  columns.forEach((col) => {
    const name = col.headerName as string;
    headerCount.set(name, (headerCount.get(name) ?? 0) + 1);
  });
  columns = columns.map((col) => {
    const name = col.headerName as string;
    const { _tableName: tableName, _relationKey: relationKey, _fromListOrCustom: fromListOrCustom, ...rest } = col as ColMeta;
    if (fromListOrCustom || (headerCount.get(name) ?? 0) <= 1) {
      return { ...rest, _fromListOrCustom: fromListOrCustom };
    }
    const relationLabel = relationKey && translations ? (translateRelation(relationKey, translations) || relationKey) : null;
    const prefix = relationLabel || tableName || '';
    const newHeaderName = prefix ? `${prefix} / ${name}` : name;
    return { ...rest, headerName: newHeaderName, minWidth: Math.max(rest.minWidth ?? 0, newHeaderName.length * 10 + 15) };
  });
  const finalHeaderCount = new Map<string, number>();
  columns.forEach((col) => {
    const name = col.headerName as string;
    finalHeaderCount.set(name, (finalHeaderCount.get(name) ?? 0) + 1);
  });
  columns = columns.map((col) => {
    const name = col.headerName as string;
    const fromListOrCustom = (col as ColMeta)._fromListOrCustom;
    if (fromListOrCustom || (finalHeaderCount.get(name) ?? 0) <= 1) return col;
    const field = col.field as string;
    const fallbackPrefix = field.includes('.') ? field.split('.').slice(0, -1).join('.') : field;
    const newHeaderName = `${fallbackPrefix} / ${name}`;
    return { ...col, headerName: newHeaderName, minWidth: Math.max(col.minWidth ?? 0, newHeaderName.length * 10 + 15) };
  });

  columns.sort((a, b) => ((a as GridColDef & { order?: number }).order ?? 0) - ((b as GridColDef & { order?: number }).order ?? 0));
  return (
    <>
      <DataTableDefault
        getRowHeight={() => 60}
        data-testid="report-data-table"
        data={data}
        columns={columns}
        autosizeOptions={{
          includeHeaders: false,
        }}
        pagination
        autoPageSize={false}
        pageSizeOptions={[10]}
        paginationMode="server"
        pageSize={10}
        {...props}
      />
      
      <DatatableModal
        open={open}
        modalData={modalData}
        reportStructure={reportStructure}
        ID_RELATORIO={ID_RELATORIO}
        relations={relations}
        fieldRelations={fieldRelations}
        callbackOnCancel={() => setOpen(false)}
      />
    </>
  );
}
